import {
  collection,
  collectionGroup,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { db } from '../firebase';
import { Group } from '../models/group';
import { ScheduleWithGroup } from '../models/schedule';
import { toDateFormat, toFormat, toTimestamp } from '../utils/timestamp';

export const useActivity = (userId: string) => {
  const groupCollection = useMemo(() => collection(db, '/groups'), []);

  const { data: groups = [] } = useQuery(`/groups/${userId}`, async () => {
    const snapshot = await getDocs(
      query(groupCollection, where('members', 'array-contains', userId))
    );
    const result: Group[] = [];
    snapshot.forEach((d) => {
      if (d.exists()) {
        result.push({ id: d.id, ...d.data() } as Group);
      }
    });
    return result;
  });

  const startTime = useMemo(() => {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return toTimestamp(date);
  }, []);

  const endTime = useMemo(() => {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    date.setDate(date.getDate() + 6);
    return toTimestamp(date);
  }, []);

  const { data: schedules = [] } = useQuery(
    `/schedules/${userId}`,
    async () => {
      const snapshot = await getDocs(
        query(
          collectionGroup(db, 'schedules'),
          where('members', 'array-contains', userId),
          where('start', '>=', startTime),
          where('start', '<', endTime),
          orderBy('start')
        )
      );
      const result: ScheduleWithGroup[] = [];
      snapshot.forEach((d) => {
        if (d.exists()) {
          result.push({
            id: d.id,
            ...d.data(),
            groupId: d.ref.path.split('/')[1],
          } as ScheduleWithGroup);
        }
      });
      return result;
    }
  );

  const schedulesByDate = useMemo(() => {
    const groupByDate = schedules.reduce<Record<string, ScheduleWithGroup[]>>(
      (dateMap, schedule) => {
        const date = toDateFormat(schedule.start);
        if (dateMap[date]) {
          return { ...dateMap, [date]: [...dateMap[date], schedule] };
        }
        return { ...dateMap, [date]: [schedule] };
      },
      {}
    );

    return Array.from({ length: 7 }).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const format = toFormat(date);

      return {
        format,
        schedules: groupByDate[format] || [],
      };
    });
  }, [schedules]);

  return {
    groups,
    schedules,
    schedulesByDate,
  };
};
